import { useState, useMemo } from "react";
import { Users, Package, MapPin, Megaphone, Building2, Warehouse, IdCard, Plug } from "lucide-react";
import UsersTab from "./UsersTab";
import ProductsTab from "./ProductsTab";
import RoutesTab from "./RoutesTab";
import AnnouncementsTab from "./AnnouncementsTab";
import CompanyInfoTab from "./CompanyInfoTab";
import WarehousesTab from "./WarehousesTab";
import PersonnelTab from "./PersonnelTab";
import IntegrationsTab from "./IntegrationsTab";

export default function SettingsTab({ products, users, routes, onUpdate, currentUser }) {
  // Granular per-sub-tab permission gating. Sidebar shows the whole
  // "Settings" entry when the user has ANY of users/products/routes/etc
  // manage perms — but within Settings each sub-tab must still be gated
  // by its specific perm, otherwise giving `products.manage` would leak
  // every other admin surface (Users, Routes, Announcements) as well.
  // Super-admin (role=admin) bypasses everything.
  const isSuperAdmin = currentUser?.role === "admin";
  const perms = useMemo(
    () => new Set(Array.isArray(currentUser?.permissions) ? currentUser.permissions : []),
    [currentUser],
  );
  const can = (key) => isSuperAdmin || perms.has(key);

  const allSections = [
    { id: "company",       label: "Company Info",  hint: "Branding & details",     icon: Building2,  allowed: can("company.manage") },
    { id: "warehouses",    label: "Warehouses",    hint: "Depot locations",        icon: Warehouse,  allowed: can("company.manage") || can("routes.manage") },
    { id: "personnel",     label: "Personnel",     hint: "Drivers & vehicles",     icon: IdCard,     allowed: can("company.manage") || can("routes.manage") },
    { id: "users",         label: "Users",         hint: "Customers & staff",      icon: Users,      allowed: can("users.manage_hotels") || can("users.manage_staff") },
    { id: "products",      label: "Products",      hint: "Catalog & pricing",      icon: Package,    allowed: can("products.manage") },
    { id: "routes",        label: "Routes",        hint: "Delivery routes",        icon: MapPin,     allowed: can("routes.manage") },
    { id: "announcements", label: "Announcements", hint: "Banner messages",        icon: Megaphone,  allowed: can("announcements.manage") },
    // Integration keys are strictly super-admin — never granular-permission gated.
    { id: "integrations",  label: "Integrations",  hint: "Stripe & Xero",          icon: Plug,       allowed: isSuperAdmin },
  ];

  const sections = allSections.filter((s) => s.allowed);
  // Default to the first allowed section so users without `products.manage`
  // don't land on an empty pane.
  const [activeSection, setActiveSection] = useState(() => sections[0]?.id || "products");

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure your company, users, products, routes and announcements.
        </p>
      </div>

      {/* Sub-tab bar — same pattern as Dispatch (underline + icon + hint). */}
      <div className="flex gap-1 border-b border-neutral-200 flex-wrap">
        {sections.map((s) => {
          const Icon = s.icon;
          const active = activeSection === s.id;
          return (
            <button
              key={s.id}
              data-testid={`settings-subtab-${s.id}`}
              onClick={() => setActiveSection(s.id)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors -mb-px ${
                active
                  ? "border-brand-navy text-brand-navy"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{s.label}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground hidden md:inline">
                · {s.hint}
              </span>
            </button>
          );
        })}
      </div>

      {activeSection === "company" && can("company.manage") && (
        <CompanyInfoTab onUpdate={onUpdate} />
      )}
      {activeSection === "warehouses" && (can("company.manage") || can("routes.manage")) && (
        <WarehousesTab />
      )}
      {activeSection === "personnel" && (can("company.manage") || can("routes.manage")) && (
        <PersonnelTab />
      )}
      {activeSection === "users" && (can("users.manage_hotels") || can("users.manage_staff")) && (
        <UsersTab users={users} routes={routes} products={products} fetchAllData={onUpdate} />
      )}
      {activeSection === "products" && can("products.manage") && (
        <ProductsTab products={products} fetchAllData={onUpdate} />
      )}
      {activeSection === "routes" && can("routes.manage") && (
        <RoutesTab routes={routes} fetchAllData={onUpdate} />
      )}
      {activeSection === "announcements" && can("announcements.manage") && (
        <AnnouncementsTab onUpdate={onUpdate} />
      )}
      {activeSection === "integrations" && isSuperAdmin && (
        <IntegrationsTab />
      )}
    </div>
  );
}
